import Image from "next/image";

import ArrowLink from "@/blueprint/components/ArrowLink/ArrowLink";
import Card from "@/blueprint/components/Card/Card";
import Text from "@/blueprint/components/Text/Text";
import VisuallyHidden from "@/blueprint/components/VisuallyHidden/VisuallyHidden";
import { SUPPORT_CARDS } from "@/views/shop/shopContent";
import Section from "@/blueprint/components/Section/Section";
import { useTrack, type CtaId } from "@/analytics/events";

const CardCopy = ({
  title,
  body,
  ctaLabel,
  href,
  cta,
}: {
  title: string;
  body: string;
  ctaLabel: string;
  href: string;
  cta: CtaId;
}) => {
  const track = useTrack();

  return (
    <div className="relative z-10 flex max-w-2xl flex-col gap-half">
      <div className="flex flex-col gap-s">
        <Text variant="h4" as="h2">
          {title}
        </Text>
        <Text variant="paragraph-md" tone="secondary">
          {body}
        </Text>
      </div>
      {/* Figma's `Button w icon` — an ink circle holding the arrow, not a bare
          glyph (node 5590-20921). */}
      <ArrowLink
        href={href}
        tone="dark"
        direction="right"
        onClick={() => track("cta_clicked", { cta, href })}
      >
        {ctaLabel}
        <VisuallyHidden>{`: ${title}`}</VisuallyHidden>
      </ArrowLink>
    </div>
  );
};

const SupportCards = ({ position }: { position: number }) => (
  <Section
    id="support-cards"
    position={position}
    className="px-grid-margin sd:grid sd:grid-cols-2 sd:gap-grid-gutter"
  >
    <div className="hideScrollBar grid grid-cols-1 gap-grid-gutter overflow-x-auto overflow-y-clip tab:flex tab:snap-x tab:snap-mandatory tab:gap-[16px] sd:contents">
      <Card className="flex flex-col p-card-lg tab:w-[475px] tab:shrink-0 tab:snap-start sd:w-auto sd:shrink sd:snap-align-none">
        <CardCopy {...SUPPORT_CARDS.roadmap} cta="roadmap" />
        {/* Figma "Illustration" frame: constant 302:204 aspect, matic render flush
            bottom-right, track vector hand-placed per breakpoint (node 5590-20921).
            Mobile cards hug their content; tab+ cards stretch to their row height,
            with the illustration pinned to the bottom (SPACE_BETWEEN). */}
        <div className="pt-3xl tab:mt-auto">
          <div className="relative w-full [aspect-ratio:302/204]">
            <svg
              viewBox="0 0 344 182"
              fill="none"
              aria-hidden="true"
              className="absolute h-auto w-[113.6%] max-w-none [left:-56%] [top:-8.8%] tab:w-[98%] tab:[left:-32.4%] tab:[top:7.1%] sd:w-[112%] sd:[left:-63.5%] sd:[top:-13.6%] d:w-[98.9%] d:[left:-23%] d:[top:10.1%]"
            >
              <path
                d="M217.325 71.724L217.325 63.8013C217.325 28.8409 187.277 0.500018 150.211 0.500016L0.499989 0.500021M197.925 73.6986L197.925 66.274C197.925 40.0538 175.389 18.7981 147.589 18.7981L0.499987 18.7981M179.049 71.724L179.049 68.2522C179.049 51.0451 164.26 37.0961 146.016 37.0961L0.499985 37.0961M179.049 70.2181L179.049 118.199C179.049 153.159 209.097 181.5 246.163 181.5L343.5 181.5M197.925 70.2181L197.925 115.726C197.925 141.946 220.52 163.202 248.32 163.202L343.035 163.202M217.325 70.2181L217.325 113.748C217.325 130.955 232.114 144.904 250.358 144.904L343.5 144.904"
                stroke="currentColor"
                strokeLinecap="round"
                vectorEffect="non-scaling-stroke"
                className="text-stroke"
              />
            </svg>
            <Image
              src={SUPPORT_CARDS.roadmap.image}
              alt={SUPPORT_CARDS.roadmap.imageAlt}
              sizes="(min-width: 1600px) 330px, (min-width: 1280px) 330px, (min-width: 800px) 225px, 62vw"
              className="absolute bottom-0 right-0 h-auto w-[68.2%] object-contain tab:w-[56.9%] sd:w-[75.5%] d:w-[50%]"
            />
          </div>
        </div>
      </Card>
      <Card className="flex flex-col p-card-lg tab:w-[475px] tab:shrink-0 tab:snap-start sd:w-auto sd:shrink sd:snap-align-none">
        <CardCopy {...SUPPORT_CARDS.team} cta="team" />
        <div className="relative mt-3xl w-full overflow-hidden rounded-s [aspect-ratio:302/201]">
          <Image
            src={SUPPORT_CARDS.team.image}
            alt={SUPPORT_CARDS.team.imageAlt}
            fill
            sizes="(min-width: 1280px) 45vw, 90vw"
            className="object-cover"
          />
        </div>
      </Card>
    </div>
  </Section>
);

export default SupportCards;
